// <reference path="../typings/index.d.ts" />
import { Address4, Address6 } from "ip-address";
import { ByteArrayUtils } from "../Utils/ByteArrayUtils";
import { IComparable } from "../Utils/ObjUtils";

export enum IpVersion {
	IPv6,
	IPv4,
}

export class IpAddress implements IComparable {

	private static IPv4_Size = 4;
	private static IPv6_Size = 16;
	private static IPv4Regex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/g;
	private static IPv6Regex = /^(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))$/g;

	private static MICROSOFT_DEFAULT = 169;

	public static Create(ipString: string): IpAddress {
		if (!ipString)
			return null;

		ipString = ipString.trim();

		if (/\s/g.test(ipString))
			return null;

		var bytes = IpAddress.GetBytes(ipString);
		if (bytes)
			return new IpAddress(bytes);
		else
			return null;
	}

	private static GetBytes(ipString): Uint8Array {
		var array: Uint8Array = null;
		this.IPv4Regex.lastIndex = 0;
		this.IPv6Regex.lastIndex = 0;
		var match = this.IPv4Regex.exec(ipString);
		if (match) {
			let v4Addr = new Address4(ipString);
			let v4Bytes = v4Addr.toArray();
			if (v4Bytes.length == IpAddress.IPv4_Size) {
				array = new Uint8Array(IpAddress.IPv4_Size);
				for (let i = 0; i < IpAddress.IPv4_Size; ++i) {
					array[i] = v4Bytes[i];
				}
			}
			else {
				console.log("IPv4 address is not " + IpAddress.IPv4_Size + " bytes: " +
					v4Bytes.length + " bytes instead");
			}
		} else {
			match = this.IPv6Regex.exec(ipString);
			if (match) {
				let v6Addr = new Address6(ipString);
				let v6Bytes = v6Addr.toByteArray();
				if (v6Bytes.length <= IpAddress.IPv6_Size) {
					array = new Uint8Array(IpAddress.IPv6_Size);
					// Missing leading bytes are assumed to be zero
					let offset = IpAddress.IPv6_Size - v6Bytes.length;
					for (let i = 0; i < v6Bytes.length; ++i) {
						array[i + offset] = v6Bytes[i];
					}
				}
				else {
					console.log("IPv6 address is not " + IpAddress.IPv6_Size + " bytes: " +
								v6Bytes.length + " bytes instead");
				}
			}
		}

		return array;
	}

	public static CreateFromBytes(bytes: Uint8Array): IpAddress {
		if (bytes == null)
			return null;

		if (bytes.length != 4 && bytes.length != 16)
			return null;

		return new IpAddress(bytes);
	}

    public static CreateFromOctets(A: number, B: number, C: number, D: number): IpAddress {
        var ipAddress: IpAddress = IpAddress.CreateByVersion(IpVersion.IPv4);
        ipAddress.Bytes[0] = A;
        ipAddress.Bytes[1] = B;
        ipAddress.Bytes[2] = C;
        ipAddress.Bytes[3] = D;

        return ipAddress;
    }

	/// <summary>
	/// Creates "null" IP address of the specified version
	/// </summary>
	/// <param name="ipVer">IP version</param>
	/// <returns>New IpAddress object</returns>
	public static CreateByVersion(ipVer: IpVersion): IpAddress {
		return new IpAddress(null, ipVer);
	}


	private _bytes: Uint8Array;
	public get Bytes(): Uint8Array {
		return this._bytes;
	}
	public set Bytes(val: Uint8Array) {
		this._bytes = val;
	}

	constructor(bytes?: Uint8Array, ipVer?: IpVersion) {
		this.Bytes = bytes;

		if (!bytes && ipVer) {
			if (ipVer == IpVersion.IPv4)
				this.Bytes = new Uint8Array(IpAddress.IPv4_Size);
			else if (ipVer == IpVersion.IPv6)
				this.Bytes = new Uint8Array(IpAddress.IPv6_Size);
		}
	}

	public get IpVersion(): IpVersion {
		if (this.Bytes.length == IpAddress.IPv6_Size)
			return IpVersion.IPv6;
		else
			return IpVersion.IPv4;
	}

	public Clone(): IpAddress {
		var bytes = new Uint8Array(this.Bytes);
		return new IpAddress(bytes);
	}

	public Equals(obj: any): boolean {
		if (obj == null)
			return false;
		if (!(obj instanceof IpAddress))
			return false;
		var other = <IpAddress> obj;
		if (this.Bytes.length != other.Bytes.length)
			return false;

		return (ByteArrayUtils.Compare(this.Bytes, other.Bytes) == 0);
	}

	public GetHashCode(): number {
		return ByteArrayUtils.ComputeHashCode(this.Bytes);
	}

	public toString(): string {
		var result = '';

		if (this.IpVersion == IpVersion.IPv4) {
			for (var i = 0; i < 4; i++) {
				result += this.Bytes[i];
				if (i < 3)
					result += '.';
			}
		} else if (this.IpVersion == IpVersion.IPv6) {
			let tmpBytes: number[] = [];
			for (var i = 0; i < 16; ++i) {
				tmpBytes.push(this.Bytes[i]);
			}
			return Address6.fromByteArray(tmpBytes).correctForm();
		}

		return result;
	}

	public ToString(): string {
		return this.toString();
	}

	public CompareTo(other: IpAddress): number {
		if (other == null)
			return 1;
		else if (this.Bytes.length < other.Bytes.length)
			return -1;
		else if (this.Bytes.length > other.Bytes.length)
			return 1;
		else
			return ByteArrayUtils.Compare(this.Bytes, other.Bytes);
	}

	public Increment(): boolean {
		return ByteArrayUtils.Increment(this.Bytes);
	}

	public Decrement(): boolean {
		return ByteArrayUtils.Decrement(this.Bytes);
	}

	public get IsNull(): boolean {

		if (!this.Bytes)
			return true;

		var isNull = true;

		for (var i = 0; i < this.Bytes.length; i++) {
			var ipByte = this.Bytes[i];
			if (ipByte == 0)
				continue;

			isNull = false;
			break;
		}

		return isNull;
	}

	public get IsValid() : boolean{
		if (this.IsNull)
			return false;

		if ((this.Bytes.length != IpAddress.IPv4_Size) && (this.Bytes.length != IpAddress.IPv6_Size))
			return false;

		return true;
	}

	public get IsBroadcast() : boolean {
		if (!this.IsValid)
			return false;

		var isBroadcast = true;

		// VERIFY each BYTE of ADDRESS
		for (var i = 0; i < this.Bytes.length; i++) {
			var ipByte = this.Bytes[i];
			if (ipByte == 255)
				continue;

			isBroadcast = false;
			break;
		}

		return isBroadcast;
	}

	public get IsPotentialBroadcast() : boolean {
		if (!this.IsValid)
			return false;

		var isBroadcast = false;

		// VERIFY each BYTE of ADDRESS
		for (var i = 0; i < this.Bytes.length; i++) {
			var ipByte = this.Bytes[i];
			if (ipByte != 255)
				continue;

			isBroadcast = true;
			break;
		}

		return isBroadcast;
	}

	public get IsLoopBack() : boolean {
		if (!this.IsValid)
			return false;

		if (this.IpVersion == IpVersion.IPv4 && this.Bytes[0] == 127)
			return true;
		else if (this.IpVersion == IpVersion.IPv6)
		{
			for (var i = 0; i < 15; i++) {
				return false;
			}
			if (this.Bytes[15] == 1)
				return true;
		}

		return false;
	}

	public get IsMicrosoftDefault():boolean {
		if (!this.IsValid)
			return false;

		// CHECK first BYTE to determine if the address is a DEFAULT Microsoft IP
		try
		{
			if (this.Bytes[0] == IpAddress.MICROSOFT_DEFAULT)
				return true;
		}
		catch(ex){
			//...
		}

		return false;
	}

	public get IsMulticast(): boolean {
		if (!this.IsValid)
			return false;

		//// CHECK IPv6
		//if (this.IpVersion == IpVersion.IPv6) {
		//	try
		//	{
		//		dotNetIP = <IPAddress>this;
		//		if (dotNetIP != null)
		//			return (dotNetIP.IsIPv6Multicast);
		//	}
		//	catch
		//		{
		//		//...
		//	}
		//	return false;
		//}

		// CHECK IPv4
		var isMulticast = false;

		// *********************************************************************
		// IPv4 Multicast Address Space Registry
		// http://www.iana.org/assignments/multicast-addresses/multicast-addresses.xml
		// 
		// Host Extensions for IP Multicasting [RFC1112] specifies the extensions
		// required of a host implementation of the Internet Protocol (IP) to
		// support multicasting.  The multicast addresses are in the range
		// 224.0.0.0 through 239.255.255.255. Address assignments are listed below.

		// The range of addresses between 224.0.0.0 and 224.0.0.255, inclusive,
		// is reserved for the use of routing protocols and other low-level
		// topology discovery or maintenance protocols, such as gateway discovery
		// and group membership reporting.  Multicast routers should not forward
		// any multicast datagram with destination addresses in this range,
		// regardless of its TTL.
		// Specific multicast address assignments
		// 224.0.0.0   - 224.0.0.255 (224.0.0/24)                Local Network Control Block
		// 224.0.1.0   - 224.0.1.255 (224.0.1/24)                Internetwork Control Block
		// 224.0.2.0   - 224.0.255.255                           AD-HOC Block
		// 224.1.0.0   - 224.1.255.255 (224.1/16)                RESERVED
		// 224.2.0.0   - 224.2.255.255 (224.2/16)                SDP/SAP Block
		// 224.3.0.0   - 224.4.255.255 (224.3/16, 224.4/16)      AD-HOC Block II
		// 224.5.0.0   - 224.251.255.255 (251/16s)               RESERVED
		// 224.252.0.0 - 224.255.255.255 (224.252/14))           DIS Transient Block
		// 225.0.0.0   - 231.255.255.255                         RESERVED
		// 232.0.0.0   - 232.255.255.255 (232/8)                 Source-Specific Multicast Block
		// 233.0.0.0   - 233.255.255.255 (233/8)                 GLOP Block
		// 233.252.0.0 - 233.255.255.255 (233.252/14)            AD-HOC Block III
		// 234.0.0.0   - 234.255.255.255                         Unicast-Prefix-based IPv4 Multicast Addresses
		// 235.0.0.0   - 238.255.255.255                         Scoped Multicast Ranges (Reserved)
		// 239.0.0.0   - 239.255.255.255 (239/8)                 Scoped Multicast Ranges (Organization-Local Scope)

		// *********************************************************************

		if (this.Bytes.length >= 1) {
			var A = this.Bytes[0];

			// MULTI-CAST addresses are in the range 224-239 (as described above)
			if ((A >= 224) && (A <= 239))
				isMulticast = true;
		}

		return (isMulticast);
	}

	public get IsValidDiscoveryAddress():boolean {
		var isValid = true;

		if (this.IsNull)           // NULL
			isValid = false;
		else if (this.IsLoopBack)  // LOOPBACK
			isValid = false;
		else if (this.IsMulticast) // MULTICAST
			isValid = false;
		else if (this.IsBroadcast) // BROADCAST
			isValid = false;

		return (isValid);
	}

	public static compare(a, b){
		var aNum = IpAddress.toNumber(a);
		var bNum = IpAddress.toNumber(b);

		if (aNum < bNum) {
			return -1;
		}
		if (aNum > bNum) {
			return 1;
		}
		return 0;
	}

	public static toNumber(ip) : number {
		var d = ip.split('.');
		var n = d[0] * Math.pow(256, 3);
		n += d[1] * Math.pow(256, 2);
		n += d[2] * 256;
		n += +d[3];
		return n;
	}
}
